import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/auth/AuthProvider'
import { Mascot } from '@/components/ui/Mascot'

type TFn = (key: string, fallback: string) => string

function friendlyError(err: unknown, t: TFn): string {
  const code = (err as { code?: string })?.code || ''
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return t('auth.checkCredentials', 'Unable to log in. Please check your credentials.')
    case 'auth/invalid-email':
      return t('auth.validation.invalidEmail', 'Please enter a valid email')
    case 'auth/too-many-requests':
      return t('settings.changePassword.errors.tooManyRequests', 'Too many attempts. Please try again later.')
    case 'auth/account-exists-with-different-credential':
      // Cross-provider dead end (audit #5): tell them WHICH way out.
      return t('webapp.authErrors.accountExists', 'An account already exists for this email with a different sign-in method. Try the method you used in the app — email & password, Google, or Apple.')
    case 'auth/popup-blocked':
      return t('webapp.authErrors.popupBlocked', 'Your browser blocked the sign-in window. Allow pop-ups for this site and try again.')
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return ''
    default:
      return t('webapp.log.error', 'Something went wrong. Please try again.')
  }
}

export function SignIn() {
  const { t } = useTranslation()
  const { signInEmail, signInGoogle, signInApple, resetPassword } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [noticeHint, setNoticeHint] = useState('')

  async function run(fn: () => Promise<void>) {
    setBusy(true)
    setError('')
    setNotice('')
    setNoticeHint('')
    try {
      await fn()
      // On success the auth listener flips state and the router redirects.
    } catch (err) {
      const msg = friendlyError(err, t)
      if (msg) setError(msg)
    } finally {
      setBusy(false)
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    void run(() => signInEmail(email.trim(), password))
  }

  async function onForgot() {
    if (!email.trim()) {
      setError(t('auth.enterEmailFirst', 'Enter your email above first, then tap “Forgot password”.'))
      return
    }
    await run(async () => {
      const trimmedEmail = email.trim()
      await resetPassword(trimmedEmail)
      // Email-enumeration protection is ON, so this resolves (and sends nothing)
      // for an address with no account — don't promise an email that won't come.
      setNotice(
        t('auth.checkInboxForReset', {
          email: trimmedEmail,
          defaultValue: "If this email is eligible for a password reset, we'll send a link. Check your spam folder too.",
        }),
      )
      // 4.38 U1: static Apple / Google hint (never look up sign-in methods — that
      // would defeat the protection); both buttons sit on this card.
      setNoticeHint(
        t('auth.resetSocialHint', {
          apple: t('authSheet.continueWithApple', 'Continue with Apple'),
          google: t('authSheet.continueWithGoogle', 'Continue with Google'),
          defaultValue:
            'Usually sign in with Apple or Google? Use {{apple}} or {{google}} instead — you may not have a password to reset.',
        }),
      )
    })
  }

  return (
    <div className="flex h-full w-full items-center justify-center bg-bg p-md">
      <div className="w-full max-w-md animate-fade-in rounded-xl border border-border bg-card p-xl shadow-lg">
        <div className="mb-lg flex flex-col items-center gap-sm">
          <Mascot pose="waving" size={96} animation="float" />
          <h1 className="text-h2 font-bold text-text">KidneyPal</h1>
          <p className="text-center text-body-sm text-text-secondary">
            {t('webapp.authSubtitle', 'Sign in to your KidneyPal account')}
          </p>
        </div>

        <form onSubmit={onSubmit} className="flex flex-col gap-md">
          <label className="flex flex-col gap-xs">
            <span className="text-body-sm font-medium text-text-secondary">
              {t('auth.email', 'Email')}
            </span>
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-lg border border-border bg-bg-elevated px-md py-sm text-body text-text outline-none focus:border-primary"
            />
          </label>

          <label className="flex flex-col gap-xs">
            <span className="text-body-sm font-medium text-text-secondary">
              {t('auth.password', 'Password')}
            </span>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="rounded-lg border border-border bg-bg-elevated px-md py-sm text-body text-text outline-none focus:border-primary"
            />
          </label>

          {error ? <p className="text-body-sm text-trigger">{error}</p> : null}
          {notice ? <p className="text-body-sm text-safe">{notice}</p> : null}
          {noticeHint ? <p className="text-body-sm text-text-secondary">{noticeHint}</p> : null}

          <button
            type="submit"
            disabled={busy}
            className="mt-xs rounded-lg bg-primary px-md py-sm text-button font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
          >
            {t('auth.signIn', 'Sign In')}
          </button>

          <button
            type="button"
            onClick={() => void onForgot()}
            disabled={busy}
            className="text-body-sm text-primary hover:underline"
          >
            {t('auth.forgotPassword', 'Forgot password?')}
          </button>
        </form>

        <div className="my-lg flex items-center gap-md">
          <span className="h-px flex-1 bg-border" />
          <span className="text-caption text-text-muted">{t('common.or', 'or')}</span>
          <span className="h-px flex-1 bg-border" />
        </div>

        <div className="flex flex-col gap-sm">
          <button
            onClick={() => void run(signInGoogle)}
            disabled={busy}
            className="rounded-lg border border-border bg-bg-elevated px-md py-sm text-button font-medium text-text hover:bg-bg-secondary disabled:opacity-60"
          >
            {t('authSheet.continueWithGoogle', 'Continue with Google')}
          </button>
          <button
            onClick={() => void run(signInApple)}
            disabled={busy}
            className="rounded-lg border border-border bg-bg-elevated px-md py-sm text-button font-medium text-text hover:bg-bg-secondary disabled:opacity-60"
          >
            {t('authSheet.continueWithApple', 'Continue with Apple')}
          </button>
        </div>

        <p className="mt-lg text-center text-caption text-text-muted">
          {t(
            'webapp.authNoSignup',
            'New to KidneyPal? Create your account at start.kidneypal.app or in the mobile app, then sign in here.',
          )}
        </p>
        <p className="mt-sm text-center text-caption text-text-muted">
          {t(
            'webapp.authPaidNoAccount',
            'Paid on start.kidneypal.app but never set up your login? Go there again and use the same email. It skips the payment and takes you to setting up your login.',
          )}
        </p>
      </div>
    </div>
  )
}
